/**
 * Data parsing functions for high-level metrics charts
 * Simple functional approach focused on getting chart data working
 */

import * as _ from "lodash";

import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import type { ChartData } from "../../../../utils/types";
import { CHART_COLORS } from "../config/consts";
import type { IDataHandlerContext } from "../types";

const METRIC_API_MAP = {
    "monthly-visits": "Visits",
    "pages-per-visit": "PagesPerVisit",
    "bounce-rate": "BounceRate",
    "visit-duration": "VisitDuration",
    "unique-visitors": "UniqueUsers",
} as const;

/**
 * Parse server data to chart format - simplified version that just works
 */
export const parseServerDataBase = (rawData: any, context: IDataHandlerContext): ChartData => {
    const data = rawData;
    if (!data?.Graph) {
        return [];
    }

    const { selectedMetric, timeGranularity, legendItems } = context;

    const metricKey = _.get(METRIC_API_MAP, selectedMetric?.id || "", "Visits") as string;

    let granularityKey = "Daily";
    if (timeGranularity?.value === GranularityNames.WEEKLY) {
        granularityKey = "Weekly";
    } else if (timeGranularity?.value === GranularityNames.MONTHLY) {
        granularityKey = "Monthly";
    }

    const chartData: ChartData = [];

    // Check if we only have Total data (no Desktop/MobileWeb breakdown)
    const availableDevices = Object.keys(data.Graph);
    const hasOnlyTotal = availableDevices.length === 1 && availableDevices[0] === "Total";

    // Use only Total if that's all we have, otherwise use all device types
    const devices = hasOnlyTotal ? ["Total"] : ["Desktop", "MobileWeb", "Total"];

    devices.forEach((deviceType) => {
        const deviceData = _.get(data.Graph, deviceType);
        let dataPoints: any[] = [];

        if (metricKey === "UniqueUsers") {
            // Special handling for UniqueUsers metric - it has a different structure
            const uniqueUsersData = _.get(deviceData, `UniqueUsers.${granularityKey}.Graph`, []);
            dataPoints = uniqueUsersData;
        } else {
            // Standard handling for other metrics
            dataPoints = _.get(deviceData, `${metricKey}.${granularityKey}.Graph`, []);
        }

        if (!Array.isArray(dataPoints) || dataPoints.length === 0) {
            return;
        }

        const legendItem = legendItems?.find(
            (item) =>
                item.name === deviceType ||
                item.metric === deviceType ||
                (deviceType === "MobileWeb" && (item.name === "Mobile Web" || item.metric === "Mobile Web")),
        );

        // If we have legend items, respect the visibility setting
        // If we have no legend items (Total-only case), show the data
        if (legendItems && legendItems.length > 0 && legendItem && !legendItem.visible) {
            return;
        }

        const seriesName = deviceType === "MobileWeb" ? "Mobile Web" : deviceType;

        chartData.push({
            name: seriesName,
            color: legendItem?.color || CHART_COLORS.DEFAULT_LEGEND,
            data: dataPoints.map((point: any) => ({
                x: new Date(point.Key).getTime(),
                y: typeof point.Value === "number" ? point.Value : 0,
                ...(point.PeriodEnd && { periodEnd: new Date(point.PeriodEnd).getTime() }),
            })),
        });
    });

    return chartData;
};

/**
 * Parse period over period data - now properly implemented
 *
 * Handles two types of POP responses:
 * 1. Main endpoint POP: Graph.{Device}.{Metric}[].Values[2] format
 * 2. Unique users POP: Data.{Category}.Total[].Values[2] format
 */
export function parseServerDataPeriodOverPeriod(data: any, context: IDataHandlerContext): ChartData {
    if (!data || !data.Graph) {
        return [];
    }

    // For POP mode, we only show "Total" traffic (All traffic)
    // Colors should match the comparison component: current period (blue) and previous period (orange)
    const currentPeriodColor = CHART_COLORS.CURRENT_PERIOD; // Blue color from comparison component
    const previousPeriodColor = CHART_COLORS.PREVIOUS_PERIOD; // Orange color from comparison component

    // Check if this is a main endpoint response (visits, bounce rate, etc.)
    const hasMainEndpointStructure =
        _.get(data.Graph, "Total.Visits") ||
        _.get(data.Graph, "Total.BounceRate") ||
        _.get(data.Graph, "Total.PagesPerVisit") ||
        _.get(data.Graph, "Total.AvgVisitDuration");

    if (hasMainEndpointStructure) {
        return parseMainEndpointPeriodOverPeriodSimplified(data, context, previousPeriodColor, currentPeriodColor);
    }

    // Check if this is a unique users endpoint response
    const hasUniqueUsersStructure = _.get(data.Graph, "Total.Key") && Array.isArray(_.get(data.Graph, "Total.Values"));

    if (hasUniqueUsersStructure) {
        return parseUniqueUsersPeriodOverPeriodSimplified(data, context, previousPeriodColor, currentPeriodColor);
    }

    return [];
}

/**
 * Simplified parser for main endpoint POP data - only shows Total (All traffic)
 */
function parseMainEndpointPeriodOverPeriodSimplified(
    data: any,
    context: IDataHandlerContext,
    previousPeriodColor: string,
    currentPeriodColor: string,
): ChartData {
    const { selectedMetric, legendItems } = context;
    const metricKey = _.get(METRIC_API_MAP, selectedMetric?.id || "", "Visits");

    const chartData: ChartData = [];

    // Only process "Total" traffic (All traffic)
    const totalData = _.get(data.Graph, "Total");
    const metricDataPoints = _.get(totalData, metricKey, []);

    if (!Array.isArray(metricDataPoints) || metricDataPoints.length === 0) {
        return [];
    }

    // Check if Total should be visible (legend filtering)
    const totalLegendItem = legendItems?.find((item) => item.name === "Total" || item.metric === "Total");

    if (totalLegendItem && !totalLegendItem.visible) {
        return [];
    }

    // Create two series with normalized timestamps
    const previousPeriodData: Array<{ x: number; y: number; originalTimestamp?: number }> = [];
    const currentPeriodData: Array<{ x: number; y: number; originalTimestamp?: number }> = [];

    // First, collect all current period timestamps to use as the base
    const currentPeriodTimestamps: number[] = [];

    metricDataPoints.forEach((dataPoint: any) => {
        if (dataPoint.Values && dataPoint.Values.length >= 2) {
            const currentValue = dataPoint.Values[1];
            if (currentValue?.Key) {
                currentPeriodTimestamps.push(new Date(currentValue.Key).getTime());
            }
        }
    });

    // Now process data points using current period timestamps for both series
    metricDataPoints.forEach((dataPoint: any, index: number) => {
        if (dataPoint.Values && dataPoint.Values.length >= 2 && index < currentPeriodTimestamps.length) {
            const previousValue = dataPoint.Values[0];
            const currentValue = dataPoint.Values[1];
            const normalizedTimestamp = currentPeriodTimestamps[index];

            // Use the same timestamp (current period) for both series
            if (previousValue?.Key && typeof previousValue.Value === "number") {
                previousPeriodData.push({
                    x: normalizedTimestamp, // Use current period timestamp for chart positioning
                    y: previousValue.Value,
                    originalTimestamp: new Date(previousValue.Key).getTime(), // Keep original timestamp for tooltip
                });
            }

            if (currentValue?.Key && typeof currentValue.Value === "number") {
                currentPeriodData.push({
                    x: normalizedTimestamp,
                    y: currentValue.Value,
                });
            }
        }
    });

    // Add previous period series (orange)
    if (previousPeriodData.length > 0) {
        chartData.push({
            name: "Previous Period",
            color: previousPeriodColor,
            data: previousPeriodData,
        });
    }

    // Add current period series (blue)
    if (currentPeriodData.length > 0) {
        chartData.push({
            name: "Current Period",
            color: currentPeriodColor,
            data: currentPeriodData,
        });
    }

    return chartData;
}

/**
 * Simplified parser for unique users POP data - only shows Total (All traffic)
 */
function parseUniqueUsersPeriodOverPeriodSimplified(
    data: any,
    context: IDataHandlerContext,
    previousPeriodColor: string,
    currentPeriodColor: string,
): ChartData {
    const chartData: ChartData = [];

    // Only process "Total" traffic
    const totalData = _.get(data.Graph, "Total");

    if (!totalData || !Array.isArray(totalData.Values)) {
        return [];
    }

    const { legendItems } = context;

    // Check if Total should be visible (legend filtering)
    const totalLegendItem = legendItems?.find((item) => item.name === "Total" || item.metric === "Total");

    if (totalLegendItem && !totalLegendItem.visible) {
        return [];
    }

    // Create two series with normalized timestamps
    const previousPeriodData = [];
    const currentPeriodData = [];

    // First, collect all current period timestamps to use as the base
    const currentPeriodTimestamps: number[] = [];

    totalData.Values.forEach((dataPoint: any) => {
        if (dataPoint.Values && dataPoint.Values.length >= 2) {
            const currentValue = dataPoint.Values[1];
            if (currentValue?.Key) {
                currentPeriodTimestamps.push(new Date(currentValue.Key).getTime());
            }
        }
    });

    // Now process data points using current period timestamps for both series
    totalData.Values.forEach((dataPoint: any, index: number) => {
        if (dataPoint.Values && dataPoint.Values.length >= 2 && index < currentPeriodTimestamps.length) {
            const previousValue = dataPoint.Values[0];
            const currentValue = dataPoint.Values[1];
            const normalizedTimestamp = currentPeriodTimestamps[index];

            // Use the same timestamp (current period) for both series
            if (previousValue?.Key && typeof previousValue.Value === "number") {
                previousPeriodData.push({
                    x: normalizedTimestamp, // Use current period timestamp for chart positioning
                    y: previousValue.Value,
                    originalTimestamp: new Date(previousValue.Key).getTime(), // Keep original timestamp for tooltip
                });
            }

            if (currentValue?.Key && typeof currentValue.Value === "number") {
                currentPeriodData.push({
                    x: normalizedTimestamp,
                    y: currentValue.Value,
                    originalTimestamp: normalizedTimestamp, // For current period, original equals normalized
                });
            }
        }
    });

    // Add previous period series (orange)
    if (previousPeriodData.length > 0) {
        chartData.push({
            name: "Previous Period",
            color: previousPeriodColor,
            data: previousPeriodData,
        });
    }

    // Add current period series (blue)
    if (currentPeriodData.length > 0) {
        chartData.push({
            name: "Current Period",
            color: currentPeriodColor,
            data: currentPeriodData,
        });
    }

    return chartData;
}
